export type TBeneficiaryBankDetails = {
  bank?: string | null
  accountNumber?: string | null
  [key: string]: any
}

/**
 * Bank details are missing once the party is loaded but is lacking either a
 * bank or an account number. The party-loaded guard prevents false positives
 * while the party is still being fetched.
 */
export const hasMissingBankDetails = (isPartyLoaded: boolean, bankDetails: TBeneficiaryBankDetails): boolean =>
  isPartyLoaded && (!bankDetails.bank || !bankDetails.accountNumber)

/**
 * Determines whether a beneficiary's payable amount should be forced to zero.
 *
 * This happens when the beneficiary is already settled, or when an unreconciled
 * invoice would pay out to a party that has no bank account details.
 *
 * Deposits "Managed by reOS" (transfer === true) route to a reOS-managed deposit
 * wallet rather than the party's bank account, so missing party bank details must
 * never zero their amount. The same applies to wallet transfers (e.g. owner wallet
 * transfers), which pay into a reOS wallet rather than an external bank account.
 */
export const shouldZeroBeneficiaryAmount = (params: {
  isSettled: boolean
  isManagedByReos: boolean
  isWalletTransfer?: boolean
  reconStatus: string
  isPartyLoaded: boolean
  bankDetails: TBeneficiaryBankDetails
}): boolean => {
  const { isSettled, isManagedByReos, isWalletTransfer = false, reconStatus, isPartyLoaded, bankDetails } = params

  const paysToBankAccount = !isManagedByReos && !isWalletTransfer
  const isUnreconciledWithoutBankDetails =
    paysToBankAccount && String(reconStatus) === 'Unreconciled' && hasMissingBankDetails(isPartyLoaded, bankDetails)

  return isSettled || isUnreconciledWithoutBankDetails
}

/**
 * A deposit that is "Self-managed by Owner" (transfer === false, managed by the
 * owner) pays the deposit out to the owner's bank account. When the owner has no
 * bank details there is nowhere to pay, so the amount should be zeroed.
 *
 * Mirrors the condition used by DepositBeneficiary's `managedByDescription` so the
 * zeroed amount and the "No bank account saved" label always agree.
 */
export const isDepositManagedByOwnerWithoutBankDetails = (params: {
  transfer: boolean
  managedByValue?: 'owner' | 'agency'
  propertyOwnerId?: string | null
  propertyOwnerBankDetails?: TBeneficiaryBankDetails | null
}): boolean => {
  const { transfer, managedByValue = 'owner', propertyOwnerId, propertyOwnerBankDetails } = params

  const isManagedByOwner = !transfer && managedByValue === 'owner' && typeof propertyOwnerId !== 'undefined'
  if (!isManagedByOwner) return false

  return hasMissingBankDetails(true, propertyOwnerBankDetails ?? {})
}
